import {
  faBuilding,
  faCalendarAlt,
  faCalendarPlus,
  faClock,
  faFileInvoice,
  faLocationDot,
  faPlusCircle,
  faTrash,
  faUser,
  faVideo,
  faWandMagicSparkles
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  type MeetingLocationData,
  MeetingLocationType,
  useNewMeetingMutation
} from '~/api/mutations/action'
import { getApiError } from '~/api/problem'
import { useAgency } from '~/api/queries/agency'
import { useCompany } from '~/api/queries/company'
import { useOrder } from '~/api/queries/order'
import {
  DateInput,
  Dropdown,
  Form,
  InputCol,
  InputLine,
  Submit,
  TextareaInput,
  TextInput
} from '~/components/Form'
import { AddressInput } from '~/components/Form/Input/AddressInput'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { ToggleInput } from '~/components/Form/Input/ToggleInput'
import { SubmitAlignment } from '~/components/Form/SubmitAlignment'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { Toggle } from '~/components/Global/Interactions/Toggle/Toggle'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import {
  durationContainer,
  locationAddress,
  locationAddressDetail,
  locationContainer
} from '~/components/Scopes/Actions/MeetingAction.css'
import { AddressBookPicker } from '~/domains/actions/components/AddressBookPicker'
import { MeetingInvitationFields } from '~/domains/actions/components/MeetingAction/MeetingInvitationFields'
import {
  MeetingLocationTypeInput,
  type MeetingLocationTypeOption
} from '~/domains/actions/components/MeetingAction/MeetingLocationTypeInput'
import { ActionErrorType } from '~/domains/actions/errors'
import {
  type MeetingFormValues,
  meetingSchema
} from '~/domains/actions/forms/meetingSchema'
import { meetingAttendees } from '~/domains/actions/utils/meetingAttendees'
import { providers } from '~/domains/calendar/providers'
import { useCalendarIdentityQuery } from '~/domains/calendar/queries/calendarQueries'
import { formatAddress } from '~/domains/common/utils'
import type { CompanyHead } from '~/domains/companies/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { useContactsHead } from '~/domains/contacts/queries/useContactHead'
import type { ContactHeadData } from '~/domains/contacts/types'
import { OrdersAutocomplete } from '~/domains/orders/components/OrdersAutocomplete'
import { useAssigneeOptions } from '~/domains/tasks/hooks/useAssigneeOptions'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { useActiveAgency } from '~/domains/users/hooks/useActiveAgency'
import { toast } from '~/utils'
import { AsideActionContext, AuthContext } from '~/utils/contextes'
import { AsideActionsItems } from './AsideActionsItems'
import { useCloseAction } from './hooks/useCloseAction'

const calculateDefaultFromDatetime = (): Date => {
  const date = new Date()
  const minutes = date.getMinutes()
  const minuteToNextQuarter = 15 - (minutes % 15)
  date.setMinutes(minutes + minuteToNextQuarter)
  return date
}

const durationToMinutes = (duration: string): number => {
  if (duration.endsWith('h')) {
    return Number.parseInt(duration.replace('h', ''), 10) * 60
  }
  return Number.parseInt(duration.replace('min', ''), 10)
}

const calculateToDateTime = (date: Date, duration: string): Date => {
  const durationMinutes = durationToMinutes(duration)
  const toDate = new Date(
    new Date(date).getTime() + durationMinutes * 60 * 1000
  )
  return toDate
}

const durationOptions = [
  { label: '5 min', value: '5min' },
  { label: '10 min', value: '10min' },
  { label: '15 min', value: '15min' },
  { label: '20 min', value: '20min' },
  { label: '30 min', value: '30min' },
  { label: '40 min', value: '40min' },
  { label: '50 min', value: '50min' },
  { label: '1 h', value: '1h' },
  { label: '2 h', value: '2h' },
  { label: '3 h', value: '3h' },
  { label: '4 h', value: '4h' },
  { label: '5 h', value: '5h' }
]

const apiErrors = {
  1: 'Une adresse doit être sélectionnée ou renseignée',
  2: 'Le candidat renseigné est invalide',
  3: 'Le contact renseigné est invalide',
  4: 'La commande renseignée est invalide',
  10: "Le lieu sélectionné n'est pas rattaché à ce rendez-vous (agence, commande ou participant)",
  20: "L'adresse sélectionnée est invalide",
  35: "Aucune adresse n'est renseignée pour le lieu sélectionné",
  999: 'Une erreur est survenue lors de la planification du rendez-vous'
}

type DurationInputProps = InputInFormProps & {
  name: string
}

const DurationInput = ({ watch, setValue, register }: DurationInputProps) => {
  if (!watch || !setValue || !register) {
    throw new Error('watch and setValue are required')
  }
  const { setIsDirty } = useContext(AsideActionContext)
  const [duration, setDuration] = useState<string>('30min')
  const [useDurationMode, setUseDurationMode] = useState<boolean>(true)
  const defaultFromDatetime = useMemo(() => calculateDefaultFromDatetime(), [])
  const defaultToDatetime = useMemo(
    () => calculateToDateTime(defaultFromDatetime, '30min'),
    [defaultFromDatetime]
  )

  const fromDatetime = watch('fromDatetime')

  const onToggleOffDurationMode = () => {
    setUseDurationMode(false)
    setIsDirty(true)
    if (!fromDatetime) {
      return
    }
    const durationMinutes = durationToMinutes(duration)
    const toDate = new Date(
      new Date(fromDatetime).getTime() + durationMinutes * 60 * 1000
    )
    setValue('toDatetime', toDate, {
      shouldDirty: false,
      shouldValidate: false
    })
  }

  const setToDatetimeWithDuration = useCallback(
    (date: Date, duration: string) => {
      const toDate = calculateToDateTime(date, duration)
      setValue('toDatetime', toDate, {
        shouldDirty: false,
        shouldValidate: false
      })
    },
    [setValue]
  )

  const onChangeFromDatetime = (date: Date | null) => {
    if (!date || !useDurationMode) {
      return
    }
    setValue('fromDatetime', date, { shouldDirty: true })
    setIsDirty(true)
    setToDatetimeWithDuration(date, duration)
  }

  const onChangeDuration = (value: string) => {
    setDuration(value)
    setIsDirty(true)
    setToDatetimeWithDuration(fromDatetime, value)
  }

  useEffect(() => {
    setValue('fromDatetime', defaultFromDatetime, { shouldDirty: false })
    setValue('toDatetime', defaultToDatetime, {
      shouldDirty: false
    })
  }, [defaultFromDatetime, setValue, defaultToDatetime])

  return (
    <InputLine>
      <DateInput
        label="Début"
        name="fromDatetime"
        startName={useDurationMode ? undefined : 'fromDatetime'}
        endName={useDurationMode ? undefined : 'toDatetime'}
        placeholder="Choisir une date"
        required
        showTime={true}
        noPast={true}
        popperPlacement="bottom-end"
        onChange={onChangeFromDatetime}
        register={register}
        watch={watch}
        setValue={setValue}
      />
      {useDurationMode ? (
        <div className={durationContainer}>
          <Dropdown
            label="Durée"
            name="duration"
            options={durationOptions}
            defaultValue={duration}
            required
            onChange={onChangeDuration}
          />
          <Tooltip text="Utiliser une date de fin" asChild direction="top">
            <Button
              type="button"
              variation={ButtonVariation.Neutral}
              narrow
              onClick={onToggleOffDurationMode}
            >
              <FontAwesomeIcon icon={faClock} />
            </Button>
          </Tooltip>
        </div>
      ) : (
        <DateInput
          key="toDatetime-date-mode"
          label="Fin"
          name="toDatetime"
          startName="fromDatetime"
          endName="toDatetime"
          placeholder="Choisir une date"
          required
          showTime={true}
          noPast={true}
          popperPlacement="bottom-end"
          onChange={() => setIsDirty(true)}
          register={register}
          watch={watch}
          setValue={setValue}
        />
      )}
    </InputLine>
  )
}

type MeetingActionProps = {
  applicantIds?: number[]
  contactIds?: number[]
  orderContext?: {
    id: number
    willChangeStatus: boolean
  }
}

const parseId = (value: string | null | undefined): number | undefined => {
  const id = Number.parseInt(value ?? '', 10)
  return Number.isNaN(id) ? undefined : id
}

export const MeetingAction = ({
  applicantIds = [],
  contactIds = [],
  orderContext
}: MeetingActionProps) => {
  const meetingCreationMutation = useNewMeetingMutation()
  const [locationData, setLocationData] = useState<MeetingLocationData | null>(
    null
  )
  const [participants, setParticipants] = useState<string[]>([])
  const [isOnlineChecked, setIsOnlineChecked] = useState<boolean>(false)
  const [wholeDay, setWholeDay] = useState<boolean>(false)
  const [selectedOrderId, setSelectedOrderId] = useState<number>()
  const [showOrderPicker, setShowOrderPicker] = useState<boolean>(false)
  const closeAction = useCloseAction()
  const { setIsDirty } = useContext(AsideActionContext)
  const currentUser = useContext(AuthContext)
  const currentUserId = currentUser?.id ?? window.JobConfig?.user.id
  const organizerOptions = useAssigneeOptions(currentUser)
  const { activeAgency } = useActiveAgency()
  const { data: orderStatuses } = useTaxonomy('applicant_order_statuses')
  const { data: defaultSendNotification } = useConfig(
    'agenda.default_send_notification_to_participants'
  )
  const [sendNotification, setSendNotification] = useState<boolean | undefined>(
    undefined
  )
  useEffect(() => {
    if (defaultSendNotification !== undefined) {
      setSendNotification(defaultSendNotification)
    }
  }, [defaultSendNotification])

  useEffect(() => {
    if (locationData?.type === MeetingLocationType.AGENCY) {
      setLocationData({
        type: MeetingLocationType.AGENCY,
        agencyId: activeAgency?.id
      })
    }
  }, [activeAgency?.id, locationData?.type])

  const isContactTarget = applicantIds.length === 0 && contactIds.length > 0
  const targetContactsQueries = useContactsHead<
    ContactHeadData & { company: CompanyHead }
  >({
    contactIds: isContactTarget ? contactIds : [],
    additionalFields: ['company']
  })
  const targetCompanyIds = [
    ...new Set(
      targetContactsQueries
        .map((query) => query.data?.company?.id)
        .filter((id) => id !== undefined)
    )
  ]
  const targetCompanyId =
    targetCompanyIds.length === 1 ? targetCompanyIds[0] : undefined
  const canPickOrder =
    !orderContext &&
    (!isContactTarget ||
      (targetCompanyId !== undefined &&
        targetContactsQueries.every((query) => query.isSuccess)))
  const targetCount = isContactTarget ? contactIds.length : applicantIds.length
  const orderId = orderContext?.id ?? selectedOrderId

  const { data: agencyData, isLoading: isAgencyLoading } = useAgency(
    locationData?.type === MeetingLocationType.AGENCY
      ? locationData.agencyId
      : activeAgency?.id
  )
  const { data: orderData } = useOrder(orderId)
  const companyId = orderData?.company?.id
  const { data: companyData } = useCompany(companyId)
  const { data: calendarIdentity } = useCalendarIdentityQuery()
  const hasIdentity = (calendarIdentity?.uuid ?? '') !== ''
  const identityProvider = calendarIdentity
    ? providers[calendarIdentity.provider]
    : null

  const onSubmit = async (values: MeetingFormValues) => {
    const attendees = meetingAttendees({
      applicantIds,
      contactIds,
      participants: values.participants ?? [],
      locationContactId:
        locationData?.type === MeetingLocationType.CONTACT
          ? locationData.contactId
          : undefined
    })

    return meetingCreationMutation.mutateAsync(
      {
        subject: values.subject,
        fromDatetime: values.fromDatetime,
        toDatetime: values.toDatetime,
        wholeDay: values.wholeDay,
        notes: values.notes,
        location: locationData,
        orderId,
        ...attendees,
        isOnline: isOnlineChecked,
        sendNotification: hasIdentity ? sendNotification : undefined,
        assignedUserId: parseId(values.organizer),
        invitation:
          values.sendInvitation &&
          values.invitationSubject &&
          values.invitationContent
            ? {
                subject: values.invitationSubject,
                content: values.invitationContent,
                templateId: values.invitationTemplateId,
                alertDaysBefore: parseId(values.alertDaysBefore)
              }
            : undefined
      },
      {
        onSuccess: (response) => {
          closeAction(true)
          toast(
            'Rendez-vous planifié',
            targetCount > 1
              ? `${targetCount} rendez-vous ont bien été planifiés`
              : 'Le rendez-vous a bien été planifié',
            ToastType.Success
          )
          const uninvitedCount = response.uninvitedRecipients?.length ?? 0
          if (uninvitedCount > 0) {
            toast(
              'Invitation non envoyée',
              uninvitedCount > 1
                ? `${uninvitedCount} participants n'ont pas reçu l'email d'invitation (adresse manquante ou invalide)`
                : "Un participant n'a pas reçu l'email d'invitation (adresse manquante ou invalide)",
              ToastType.Warning
            )
          }
          if (typeof window.paginatorHistoriqueActions !== 'undefined') {
            window.paginatorHistoriqueActions.refresh()
          }
        },
        onError: async (error) => {
          const problem = getApiError(error)
          if (problem?.type === ActionErrorType.LimitExceeded) {
            const limit = problem.extensions.limit
            if (problem.extensions.actionType === 'email') {
              toast(
                'Limite dépassée',
                `L'invitation représente ${problem.extensions.provided} emails, au-delà de la limite de ${limit} : réduisez les participants ou n'envoyez pas l'email`,
                ToastType.Danger
              )
              return
            }
            const label = isContactTarget ? 'contact' : 'candidat'
            toast(
              'Limite dépassée',
              typeof limit === 'number'
                ? `Vous ne pouvez pas prendre rendez-vous avec plus de ${limit} ${label}${limit > 1 ? 's' : ''} à la fois`
                : `Vous ne pouvez pas prendre rendez-vous avec plus de 1 ${label} à la fois`,
              ToastType.Danger
            )
            return
          }
          if (problem?.type === ActionErrorType.RecipientInvalidEmail) {
            const email = problem.extensions.email
            toast(
              'Participant invalide',
              typeof email === 'string' && email !== ''
                ? `L'adresse « ${email} » d'un participant est invalide`
                : "Un participant n'a pas d'adresse email",
              ToastType.Danger
            )
            return
          }
          if (problem?.type === ActionErrorType.RecipientNotAllowed) {
            toast(
              'Participant non autorisé',
              "Vous n'êtes pas autorisé à ajouter l'un des participants",
              ToastType.Danger
            )
            return
          }
          if (problem?.type === ActionErrorType.RecipientOutsideOrderCompany) {
            toast(
              'Participant non autorisé',
              "Un contact participant n'appartient pas à l'entreprise de la commande",
              ToastType.Danger
            )
            return
          }

          const body = await error.response
            .json()
            .catch(() => ({}) as { code?: number; message?: string })
          toast(
            'Erreur lors de la planification du rendez-vous',
            apiErrors[(body.code ?? 999) as keyof typeof apiErrors],
            ToastType.Danger
          )
        }
      }
    )
  }

  const hasContactParticipant =
    isContactTarget ||
    locationData?.type === MeetingLocationType.CONTACT ||
    participants.some((value) => value.startsWith('/v1/contacts/'))
  const nextStatus = orderStatuses?.find(
    (status) =>
      (!hasContactParticipant && status.specialStatus === 'internal_meeting') ||
      (hasContactParticipant && status.specialStatus === 'presented')
  )?.name
  const willChangeStatus =
    orderId !== undefined &&
    applicantIds.length > 0 &&
    (orderContext?.willChangeStatus ?? true)

  const placeholderCategories = [
    ...(isContactTarget ? ['Contact', 'Entreprise'] : ['Candidat']),
    'Agence',
    'Autres',
    ...(orderId !== undefined ? ['Commande'] : [])
  ]

  const optionsLocationType: MeetingLocationTypeOption[] = [
    { label: 'À distance', value: MeetingLocationType.ONLINE, icon: faVideo },
    { label: 'Agence', value: MeetingLocationType.AGENCY, icon: faBuilding },
    ...(companyData?.contacts?.length
      ? [
          {
            label: 'Contact',
            value: MeetingLocationType.CONTACT,
            icon: faUser
          }
        ]
      : []),
    ...(orderId !== undefined
      ? [
          {
            label: 'Commande',
            value: MeetingLocationType.ORDER,
            icon: faFileInvoice
          }
        ]
      : []),
    {
      label: 'Autre adresse',
      value: MeetingLocationType.CUSTOM,
      icon: faLocationDot
    }
  ]

  return (
    <Form
      onSubmit={onSubmit}
      schema={meetingSchema}
      defaultValues={{
        organizer:
          currentUserId !== undefined ? String(currentUserId) : undefined
      }}
      onDirtyStateChange={(isDirty) => {
        if (isDirty) {
          setIsDirty(true)
        }
      }}
    >
      <AsideAction
        title="Rendez-vous"
        icon={faCalendarAlt}
        applicantIds={isContactTarget ? undefined : applicantIds}
        contactIds={isContactTarget ? contactIds : undefined}
      >
        <AsideActionsItems>
          <Button
            variation={ButtonVariation.DangerInversed}
            type="button"
            onClick={(e) => {
              e.preventDefault()
              closeAction()
            }}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
          <Submit
            buttonVariation={ButtonVariation.Primary}
            submitAlignment={SubmitAlignment.Right}
            loadingText="Planification..."
            successText="Planifié !"
            onSuccessReset={false}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faCalendarPlus} />
            Planifier
          </Submit>
        </AsideActionsItems>
        {targetCount > 1 && (
          <Alert
            variation={AlertVariation.Info}
            message={`Un rendez-vous distinct sera planifié pour chacun des ${targetCount} ${
              isContactTarget ? 'contacts' : 'candidats'
            }, avec les mêmes participants.`}
          />
        )}
        {willChangeStatus && nextStatus && (
          <Alert
            variation={AlertVariation.Info}
            icon={faWandMagicSparkles}
            message={`La planification du rendez-vous changera automatiquement le statut de ${
              applicantIds.length > 1 ? 'ces' : 'ce'
            } candidat${applicantIds.length > 1 ? 's' : ''} vers « ${nextStatus} ».`}
          />
        )}
        <InputCol>
          <TextInput
            label="Sujet"
            name="subject"
            autoFocus
            onChange={() => setIsDirty(true)}
          />

          {organizerOptions.length > 0 && (
            <Dropdown
              label="Organisateur"
              name="organizer"
              required
              options={organizerOptions}
              defaultValue={
                currentUserId !== undefined ? String(currentUserId) : undefined
              }
              onChange={() => setIsDirty(true)}
            />
          )}

          {wholeDay ? (
            <InputLine>
              <DateInput
                label="Du"
                name="fromDatetime"
                startName="fromDatetime"
                endName="toDatetime"
                placeholder="Choisir une date"
                required
                noPast
                onChange={() => setIsDirty(true)}
              />
              <DateInput
                label="Au"
                name="toDatetime"
                startName="fromDatetime"
                endName="toDatetime"
                placeholder="Choisir une date"
                required
                noPast
                onChange={() => setIsDirty(true)}
              />
            </InputLine>
          ) : (
            <DurationInput name="duration" />
          )}

          <ToggleInput
            name="wholeDay"
            label="Journée entière"
            onChange={(checked) => {
              setWholeDay(checked)
              setIsDirty(true)
            }}
          />

          {canPickOrder && !showOrderPicker && (
            <ButtonGroup>
              <Button
                type="button"
                onClick={() => setShowOrderPicker(true)}
                variation={ButtonVariation.Neutral}
                buttonSize="small"
              >
                <FontAwesomeIcon icon={faPlusCircle} />
                Commande
              </Button>
            </ButtonGroup>
          )}

          {canPickOrder && showOrderPicker && (
            <OrdersAutocomplete
              label="Commande"
              name="orderId"
              companyId={targetCompanyId}
              placeholder="Associer à une commande"
              onChange={(value) => {
                setSelectedOrderId(parseId(value))
                if (
                  locationData?.type === MeetingLocationType.ORDER ||
                  locationData?.type === MeetingLocationType.CONTACT
                ) {
                  setLocationData(null)
                }
                setIsDirty(true)
              }}
            />
          )}

          <div className={locationContainer}>
            <MeetingLocationTypeInput
              label="Lieu"
              name="locationType"
              options={optionsLocationType}
              value={locationData?.type ?? null}
              onChange={(value) => {
                if (value === MeetingLocationType.AGENCY) {
                  setLocationData({
                    type: MeetingLocationType.AGENCY,
                    agencyId: activeAgency?.id
                  })
                } else if (
                  value === MeetingLocationType.ORDER &&
                  orderId !== undefined
                ) {
                  setLocationData({
                    type: MeetingLocationType.ORDER,
                    orderId
                  })
                } else if (value === MeetingLocationType.CONTACT) {
                  setLocationData({
                    type: MeetingLocationType.CONTACT,
                    contactId: companyData?.contacts?.[0]?.id
                  })
                } else if (value === MeetingLocationType.ONLINE) {
                  setLocationData({ type: MeetingLocationType.ONLINE })
                } else if (value === MeetingLocationType.CUSTOM) {
                  setLocationData({ type: MeetingLocationType.CUSTOM })
                }
                setIsDirty(true)
              }}
            />

            {locationData?.type === MeetingLocationType.ONLINE && (
              <InputLine>
                {hasIdentity && (
                  <Toggle
                    defaultState={isOnlineChecked}
                    label={`Créer un lien ${identityProvider?.meetingService}`}
                    onChange={(checked) => {
                      setIsOnlineChecked(checked)
                      if (checked) {
                        setIsDirty(true)
                      }
                    }}
                  />
                )}
                {(!hasIdentity || !isOnlineChecked) && (
                  <TextInput
                    placeholder="Détails (lien de visio, ...)"
                    standalone
                    onChange={(e) => {
                      setLocationData({
                        type: MeetingLocationType.ONLINE,
                        details: (e.target as HTMLInputElement).value
                      })
                      setIsDirty(true)
                    }}
                  />
                )}
              </InputLine>
            )}
            {locationData?.type === MeetingLocationType.AGENCY && (
              <div className={locationAddress}>
                {isAgencyLoading ? (
                  <span className={locationAddressDetail}>
                    Chargement de l'agence...
                  </span>
                ) : (
                  agencyData && (
                    <>
                      <FontAwesomeIcon icon={faLocationDot} />
                      <span>{agencyData.name}</span>
                      <span className={locationAddressDetail}>
                        {formatAddress(agencyData.address)}
                      </span>
                    </>
                  )
                )}
              </div>
            )}

            {locationData?.type === MeetingLocationType.CONTACT && (
              <>
                <Dropdown
                  options={
                    companyData?.contacts
                      ?.filter((contact) => !contact.archived)
                      ?.map((contact) => ({
                        label: `${contact.firstname} ${contact.lastname}`,
                        value: contact.id.toString()
                      })) || []
                  }
                  name="contactId"
                  required
                  fullWidth
                  placeholder="Choisir un contact"
                  defaultValue={locationData?.contactId?.toString() ?? null}
                  onChange={(value) => {
                    setLocationData({
                      type: MeetingLocationType.CONTACT,
                      contactId: Number.parseInt(value, 10)
                    })
                    setIsDirty(true)
                  }}
                />
                {locationData.contactId && (
                  <div className={locationAddress}>
                    <FontAwesomeIcon icon={faLocationDot} />
                    <span className={locationAddressDetail}>
                      {formatAddress(
                        companyData?.contacts?.find(
                          (contact) => contact.id === locationData.contactId
                        )?.address ?? companyData?.address
                      )}
                    </span>
                  </div>
                )}
              </>
            )}

            {locationData?.type === MeetingLocationType.ORDER && orderData && (
              <div className={locationAddress}>
                <FontAwesomeIcon icon={faLocationDot} />
                <span className={locationAddressDetail}>
                  {formatAddress(
                    orderData.address ??
                      companyData?.contacts?.find(
                        (contact) => contact.id === orderData.contact.id
                      )?.address ??
                      companyData?.address
                  )}
                </span>
              </div>
            )}

            {locationData?.type === MeetingLocationType.CUSTOM && (
              <AddressInput
                placeholder="Saisissez une adresse"
                name="address"
                onChange={(address) => {
                  setLocationData({
                    type: MeetingLocationType.CUSTOM,
                    address: formatAddress(address)
                  })
                  setIsDirty(true)
                }}
              />
            )}
          </div>

          {hasIdentity && sendNotification !== undefined && (
            <Toggle
              key={String(defaultSendNotification)}
              defaultState={sendNotification}
              label="Envoyer l'invitation calendrier aux participants"
              onChange={(checked) => {
                setSendNotification(checked)
                setIsDirty(true)
              }}
            />
          )}

          <TextareaInput
            label="Notes"
            name="notes"
            rows={2}
            autoResize
            onChange={() => setIsDirty(true)}
          />

          <AddressBookPicker
            name="participants"
            label="Participants supplémentaires"
            placeholder="Ajouter des participants"
            allowFreeText
            orderCompanyId={companyId}
            types={
              applicantIds.length > 0
                ? ['user', 'contact', 'external']
                : undefined
            }
            onChange={(values) => {
              setParticipants(values)
              setIsDirty(true)
            }}
          />

          <MeetingInvitationFields
            name="invitation"
            placeholderCategories={placeholderCategories}
          />
        </InputCol>
      </AsideAction>
    </Form>
  )
}
